/**
 * `/admin/compliance` — the §8 record, as a screen.
 *
 * **The blocked attestations are the compliance record.** They carry no
 * `opportunity_id` by construction: if a record existed, the refusal did not
 * happen. They are the population an auditor samples, and the reason this
 * system can say "we asked, and we refused" rather than "we believe we hold
 * nothing controlled".
 *
 * The pattern warnings show the **field and the class, never the content**.
 * There is no column holding the matched text — the event was written without
 * it on purpose — so this screen could not display it if it tried.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { useApiMutation, useApiQuery, useApiQueryAllPages, type Row } from '@/lib/queries';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Field } from '@/components/ui/label';
import { REFERENCE_QUERY_KEY } from '@/lib/reference';
import { formatDate, formatDateTime, humanize } from '@/lib/format';

interface ComplianceReport {
  policy_version: string;
  refusal_text: string;
  attestations: {
    event_type: string;
    occurred_at: string;
    policy_version: string;
    opportunity_id: string | null;
    account_id: string | null;
    user_display_name: string;
  }[];
  pattern_warnings: {
    pattern_class: string;
    field_name: string;
    n: number;
    last_seen: string;
  }[];
  pattern_classes: Record<string, string>;
  attachment_attestations: {
    occurred_at: string;
    user_display_name: string;
    policy_version: string;
  }[];
  acknowledged: { id: string; display_name: string; email: string; acknowledged_at: string }[];
  not_acknowledged: { id: string; display_name: string; email: string }[];
  nda_expiring: { id: string; name: string; nda_on_file: boolean; nda_expiry_date: string }[];
  export_sensitive_accounts: { id: string; name: string; region: string; segment: string }[];
}

const COMPLIANCE_KEY = ['admin', 'compliance'] as const;

export function AdminCompliance(): React.ReactElement {
  const report = useApiQuery<ComplianceReport>(COMPLIANCE_KEY, '/api/admin/compliance');
  const data = report.data;

  if (report.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (report.error !== null) {
    return (
      <p className="rounded-md border border-destructive/40 bg-red-50 p-3 text-sm text-red-900">
        {report.error.message}
      </p>
    );
  }
  if (data === undefined) return <p className="text-sm text-muted-foreground">No data.</p>;

  const blocked = data.attestations.filter((row) => row.event_type === 'attestation_blocked');

  return (
    <div className="space-y-6">
      <section className="grid gap-3 sm:grid-cols-4">
        <Metric label="Policy version" value={data.policy_version} />
        <Metric label="Attestations recorded" value={String(data.attestations.length)} />
        <Metric label="Refused outright" value={String(blocked.length)} />
        <Metric
          label="Not yet acknowledged"
          value={String(data.not_acknowledged.length)}
          tone={data.not_acknowledged.length > 0 ? 'warn' : undefined}
        />
      </section>

      <RefusalText current={data.refusal_text} />

      <Panel
        title="Attestations"
        hint="Both answers are recorded. A refusal carries no opportunity, because no record was created."
      >
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-foreground">
            <tr>
              <th scope="col" className="py-1">
                When
              </th>
              <th scope="col" className="py-1">
                Who
              </th>
              <th scope="col" className="py-1">
                Answer
              </th>
              <th scope="col" className="py-1">
                Opportunity
              </th>
              <th scope="col" className="py-1">
                Policy
              </th>
            </tr>
          </thead>
          <tbody>
            {data.attestations.slice(0, 100).map((row, index) => (
              <tr key={index} className="border-t">
                <td className="py-1">{formatDateTime(row.occurred_at)}</td>
                <td className="py-1">{row.user_display_name}</td>
                <td className="py-1">
                  {row.event_type === 'attestation_blocked' ? (
                    <Badge variant="danger">
                      <ShieldAlert className="h-3 w-3" aria-hidden="true" />
                      Refused — yes or unsure
                    </Badge>
                  ) : (
                    <Badge variant="success">Proceeded — commercial only</Badge>
                  )}
                </td>
                <td className="py-1">
                  {row.opportunity_id === null ? (
                    <span className="text-xs text-muted-foreground">
                      none — nothing was created
                    </span>
                  ) : (
                    <Link
                      to={`/opportunities/${row.opportunity_id}`}
                      className="text-xs underline underline-offset-2"
                    >
                      open
                    </Link>
                  )}
                </td>
                <td className="py-1 text-xs text-muted-foreground">{row.policy_version}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      <Panel
        title="Free-text pattern warnings"
        hint="Field and pattern class only. The matched text is never stored, here or anywhere."
      >
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-foreground">
            <tr>
              <th scope="col" className="py-1">
                Pattern class
              </th>
              <th scope="col" className="py-1">
                Field
              </th>
              <th scope="col" className="py-1 text-right">
                Count
              </th>
              <th scope="col" className="py-1">
                Last seen
              </th>
            </tr>
          </thead>
          <tbody>
            {data.pattern_warnings.map((row, index) => (
              <tr key={index} className="border-t">
                <td className="py-1" title={data.pattern_classes[row.pattern_class]}>
                  {humanize(row.pattern_class)}
                </td>
                <td className="py-1 font-mono text-xs">{row.field_name}</td>
                <td className="py-1 text-right tabular-nums">{row.n}</td>
                <td className="py-1 text-xs text-muted-foreground">
                  {formatDateTime(row.last_seen)}
                </td>
              </tr>
            ))}
            {data.pattern_warnings.length === 0 && (
              <tr>
                <td colSpan={4} className="py-3 text-xs text-muted-foreground">
                  Nothing has matched. The scan warns and never blocks.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Panel>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Attachment attestations" hint="Taken on every upload, on both paths.">
          <ul className="space-y-1 text-sm">
            {data.attachment_attestations.slice(0, 25).map((row, index) => (
              <li key={index} className="flex justify-between gap-2">
                <span>{row.user_display_name}</span>
                <span className="text-xs text-muted-foreground">
                  {formatDateTime(row.occurred_at)}
                </span>
              </li>
            ))}
            {data.attachment_attestations.length === 0 && (
              <li className="text-xs text-muted-foreground">No uploads yet.</li>
            )}
          </ul>
        </Panel>

        <Panel
          title="Policy acknowledgment"
          hint="Bumping the version in Settings re-prompts everyone, immediately."
        >
          <p className="text-sm">
            <strong>{data.acknowledged.length}</strong> acknowledged ·{' '}
            <strong>{data.not_acknowledged.length}</strong> outstanding
          </p>
          {data.not_acknowledged.length > 0 && (
            <ul className="mt-2 space-y-0.5 text-xs text-muted-foreground">
              {data.not_acknowledged.map((user) => (
                <li key={user.id}>
                  {user.display_name} — {user.email}
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="NDA expiry" hint="Within 90 days, or already expired.">
          <ul className="space-y-1 text-sm">
            {data.nda_expiring.map((account) => (
              <li key={account.id} className="flex items-center justify-between gap-2">
                <Link to={`/accounts/${account.id}`} className="hover:underline">
                  {account.name}
                </Link>
                <span className="text-xs">{formatDate(account.nda_expiry_date)}</span>
              </li>
            ))}
            {data.nda_expiring.length === 0 && (
              <li className="text-xs text-muted-foreground">Nothing expiring.</li>
            )}
          </ul>
        </Panel>

        <ExportSensitivePanel accounts={data.export_sensitive_accounts} />
      </div>
    </div>
  );
}

function RefusalText({ current }: { current: string }): React.ReactElement {
  const [text, setText] = useState(current);

  const save = useApiMutation<{ updated: boolean }, void>(
    () => ({ path: '/api/admin/compliance/refusal-text', method: 'PUT', body: { text } }),
    { invalidate: [COMPLIANCE_KEY] },
  );

  return (
    <Panel
      title="Refusal text"
      hint="Shown when someone answers yes, or is not sure. It names the approved system or process, so it must never be hardcoded."
    >
      <Field label="Text">
        <Textarea rows={4} value={text} onChange={(event) => setText(event.target.value)} />
      </Field>
      <div className="mt-2 flex justify-end">
        <Button
          size="sm"
          onClick={() => save.mutate()}
          disabled={text.trim() === '' || save.isPending}
        >
          Save
        </Button>
      </div>
    </Panel>
  );
}

/**
 * `export_sensitive` is set by a person, never derived from a segment — a
 * defense account is not automatically one whose *data entry* will be
 * controlled, and deriving it would re-make the mistake §8 avoids by asking
 * about data rather than about the programme.
 */
function ExportSensitivePanel({
  accounts,
}: {
  accounts: { id: string; name: string; region: string; segment: string }[];
}): React.ReactElement {
  // Every account and program, not one page of 200: "show all" must mean all,
  // or an account past the 200th by name could never be marked (session 6).
  const allAccounts = useApiQueryAllPages<Row>(['accounts', 'all'], '/api/accounts?sort=name');
  const programs = useApiQueryAllPages<Row>(['programs', 'all'], '/api/programs');
  const [showAll, setShowAll] = useState(false);

  const toggleAccount = useApiMutation<Record<string, unknown>, { id: string; value: boolean }>(
    ({ id, value }) => ({
      path: `/api/accounts/${id}`,
      method: 'PUT',
      body: { export_sensitive: value },
    }),
    { invalidate: [COMPLIANCE_KEY, ['accounts', 'all'], REFERENCE_QUERY_KEY] },
  );

  const toggleProgram = useApiMutation<Record<string, unknown>, { id: string; value: boolean }>(
    ({ id, value }) => ({
      path: `/api/programs/${id}`,
      method: 'PUT',
      body: { export_sensitive: value },
    }),
    { invalidate: [COMPLIANCE_KEY, ['programs', 'all'], REFERENCE_QUERY_KEY] },
  );

  const accountRows = (allAccounts.data?.items ?? []).filter(
    (row) => showAll || row['export_sensitive'] === true,
  );
  const programRows = (programs.data?.items ?? []).filter(
    (row) => showAll || row['export_sensitive'] === true,
  );

  return (
    <Panel
      title="Export-sensitive accounts and programs"
      hint="Pre-selects the attestation to Yes, so a rep must actively override rather than click through."
    >
      <label className="mb-2 flex items-center gap-2 text-xs">
        <input
          type="checkbox"
          className="h-3.5 w-3.5"
          checked={showAll}
          onChange={(event) => setShowAll(event.target.checked)}
        />
        Show everything, not only the flagged ones
      </label>

      <p className="text-xs font-medium">Accounts ({accounts.length} flagged)</p>
      <ul className="mb-3 space-y-1 text-sm">
        {accountRows.map((row) => (
          <li key={String(row['id'])} className="flex items-center justify-between gap-2">
            <span>{String(row['name'])}</span>
            <label className="flex items-center gap-1.5 text-xs">
              <input
                type="checkbox"
                className="h-3.5 w-3.5"
                checked={row['export_sensitive'] === true}
                onChange={(event) =>
                  toggleAccount.mutate({ id: String(row['id']), value: event.target.checked })
                }
              />
              sensitive
            </label>
          </li>
        ))}
      </ul>

      <p className="text-xs font-medium">Programs</p>
      <ul className="space-y-1 text-sm">
        {programRows.map((row) => (
          <li key={String(row['id'])} className="flex items-center justify-between gap-2">
            <span>{String(row['name'])}</span>
            <label className="flex items-center gap-1.5 text-xs">
              <input
                type="checkbox"
                className="h-3.5 w-3.5"
                checked={row['export_sensitive'] === true}
                onChange={(event) =>
                  toggleProgram.mutate({ id: String(row['id']), value: event.target.checked })
                }
              />
              sensitive
            </label>
          </li>
        ))}
        {programRows.length === 0 && (
          <li className="text-xs text-muted-foreground">None flagged.</li>
        )}
      </ul>
    </Panel>
  );
}

function Panel({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <section className="rounded-lg border bg-card p-4">
      <h2 className="text-sm font-medium">{title}</h2>
      {hint !== undefined && <p className="mb-2 text-xs text-muted-foreground">{hint}</p>}
      {children}
    </section>
  );
}

function Metric({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'warn';
}): React.ReactElement {
  return (
    <div className="rounded-lg border bg-card p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p
        className={`text-lg font-semibold tabular-nums ${tone === 'warn' ? 'text-amber-700' : ''}`}
      >
        {value}
      </p>
    </div>
  );
}
